import {
  MAINTENANCE_INK_COLORS,
  MAINTENANCE_INK_DEFAULT_COLOR,
  MAINTENANCE_INK_DEFAULT_WIDTH,
  MAINTENANCE_INK_TEXT_FONT_RATIO,
  MAINTENANCE_INK_TEXT_MAX,
  MAINTENANCE_INK_WIDTHS,
  maintenanceInkPreviewStyle,
  maintenanceInkSanitizeText,
  type MaintenanceInkColor,
  type MaintenanceInkPoint,
  type MaintenanceInkStroke,
  type MaintenanceInkText,
  type MaintenanceInkWidth,
} from '@gb-ticket/shared-types';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  EraserIcon,
  HandIcon,
  Maximize2Icon,
  Minimize2Icon,
  PencilIcon,
  Redo2Icon,
  ScanIcon,
  TypeIcon,
  Undo2Icon,
} from 'lucide-react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import type * as pdfjs from 'pdfjs-dist';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ApiRequestError } from '@/lib/api';
import { cn } from '@/lib/utils';
import {
  isScreenLandscape,
  nextPageTurn,
  requestDeviceOrientation,
  rotatedScrollerStyle,
  type InkPageTurn,
} from '@/products/maintenance/features/protocol/protocol-ink-orientation';
import { loadMaintenancePdfDocument } from '@/products/maintenance/features/pdf/load-maintenance-pdf-document';
import {
  appendStrokePoints,
  inkPageWidth,
  isLargeContact,
  normalizePointerType,
  pageAspect,
  roleForNewPointer,
  scrollForPan,
  scrollToKeepPoint,
  shouldCancelFingerInk,
  shouldCapturePointer,
  strokeHit,
  unitPoint,
  zoomFromPinch,
  type InkPointerRole,
  type InkPointerType,
  type PanOrigin,
} from '@/products/maintenance/features/protocol/protocol-ink-gestures';
import {
  clearInkDraft,
  clearInkPageDraft,
  readInkDraft,
  writeInkPageDraft,
} from '@/products/maintenance/features/protocol/protocol-ink-draft';
import {
  emptyInkHistory,
  inkStatesEqual,
  persistableInkTexts,
  pushInkHistory,
  redoInkHistory,
  undoInkHistory,
  type InkPageHistory,
  type InkPageState,
} from '@/products/maintenance/features/protocol/protocol-ink-history';
import { useUpsertMaintenanceInkLayer } from '@/products/maintenance/features/protocol/use-maintenance-protocol';

type Tool = 'draw' | 'erase' | 'scroll' | 'text';

type Props = {
  protocolId: string;
  pdfUrl: string;
  revision: number;
  layers: { page: number; strokes: MaintenanceInkStroke[]; texts?: MaintenanceInkText[] }[];
  disabled?: boolean;
  onSaved: (revision: number) => void;
  onConflict: () => void;
};

type TrackedPointer = {
  id: number;
  type: InkPointerType;
  x: number;
  y: number;
  role: InkPointerRole;
  pan: PanOrigin | null;
  originX: number;
  originY: number;
};

type PinchGesture = {
  startDistance: number;
  startZoom: number;
  originX: number;
  originY: number;
  contentX: number;
  contentY: number;
};

type ActiveStroke = {
  pointerId: number;
  fromTouch: boolean;
  points: MaintenanceInkPoint[];
  color: MaintenanceInkColor;
  width: MaintenanceInkWidth;
};

// Document ink (blue, black, red) is part of the protocol, not the app theme.
const INK_COLOR_LABELS: Record<MaintenanceInkColor, string> = {
  '#1d4ed8': 'Blau',
  '#111827': 'Schwarz',
  '#b91c1c': 'Rot',
};

const INK_WIDTH_LABELS: Record<MaintenanceInkWidth, string> = {
  0.002: 'Fein',
  0.004: 'Normal',
  0.008: 'Dick',
};

function strokeFromActive(active: ActiveStroke): MaintenanceInkStroke {
  return { points: active.points, color: active.color, width: active.width };
}

function parseInkColor(value: string): MaintenanceInkColor | null {
  return (MAINTENANCE_INK_COLORS as readonly string[]).includes(value)
    ? (value as MaintenanceInkColor)
    : null;
}

function parseInkWidth(value: string): MaintenanceInkWidth | null {
  const width = Number(value);
  return (MAINTENANCE_INK_WIDTHS as readonly number[]).includes(width)
    ? (width as MaintenanceInkWidth)
    : null;
}

const TEXT_TAP_PX = 12;

function pageStateFor(
  protocolId: string,
  page: number,
  layers: { page: number; strokes: MaintenanceInkStroke[]; texts?: MaintenanceInkText[] }[],
): { state: InkPageState; dirty: boolean } {
  const draft = readInkDraft(protocolId)[String(page)];
  if (draft) {
    return { state: { strokes: draft.strokes, texts: draft.texts }, dirty: true };
  }
  const layer = layers.find((entry) => entry.page === page);
  return {
    state: { strokes: layer?.strokes ?? [], texts: layer?.texts ?? [] },
    dirty: false,
  };
}

function pointsFromPointer(event: React.PointerEvent<SVGSVGElement>): MaintenanceInkPoint[] {
  const rect = event.currentTarget.getBoundingClientRect();
  const native = event.nativeEvent;
  const samples =
    typeof native.getCoalescedEvents === 'function' ? native.getCoalescedEvents() : [];
  const source = samples.length > 0 ? samples : [native];
  return source.map((sample) => unitPoint(rect, sample.clientX, sample.clientY));
}

function aspectFromEvent(event: React.PointerEvent<SVGSVGElement>): number {
  const rect = event.currentTarget.getBoundingClientRect();
  return pageAspect(rect.width, rect.height);
}

export function ProtocolInkSheet({
  protocolId,
  pdfUrl,
  revision,
  layers,
  disabled,
  onSaved,
  onConflict,
}: Props) {
  const save = useUpsertMaintenanceInkLayer(protocolId);
  const saveRef = useRef(save.mutateAsync);
  useEffect(() => {
    saveRef.current = save.mutateAsync;
  }, [save.mutateAsync]);
  const initial = pageStateFor(protocolId, 1, layers);
  const [localRevision, setLocalRevision] = useState(revision);
  const revisionToSave = Math.max(revision, localRevision);
  const [pdf, setPdf] = useState<pdfjs.PDFDocumentProxy | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [tool, setTool] = useState<Tool>('draw');
  const [inkColor, setInkColor] = useState<MaintenanceInkColor>(MAINTENANCE_INK_DEFAULT_COLOR);
  const [inkWidth, setInkWidth] = useState<MaintenanceInkWidth>(MAINTENANCE_INK_DEFAULT_WIDTH);
  const [strokes, setStrokes] = useState<MaintenanceInkStroke[]>(initial.state.strokes);
  const [texts, setTexts] = useState<MaintenanceInkText[]>(initial.state.texts);
  const [dirty, setDirty] = useState(initial.dirty);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [textFontPx, setTextFontPx] = useState(14);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [pageTurn, setPageTurn] = useState<InkPageTurn>(0);
  const [stageSize, setStageSize] = useState({ width: 0, height: 0 });
  const committedRef = useRef(initial.state);
  const activeRef = useRef<ActiveStroke | null>(null);
  const eraseWorkingRef = useRef<InkPageState | null>(null);
  const draftTextRef = useRef<MaintenanceInkText | null>(null);
  const historyByPageRef = useRef(new Map<number, InkPageHistory>());
  const pointersRef = useRef(new Map<number, TrackedPointer>());
  const penSeenRef = useRef(false);
  const pinchRef = useRef<PinchGesture | null>(null);
  const zoomRef = useRef(1);
  const textElsRef = useRef(new Map<string, HTMLElement>());
  const textDragRef = useRef<{
    id: string;
    originX: number;
    originY: number;
    startX: number;
    startY: number;
    moved: boolean;
  } | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const pageBlockRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const pageCount = pdf?.numPages ?? 1;

  useEffect(() => {
    if (!editingId) {
      return;
    }
    const input = textElsRef.current.get(editingId)?.querySelector('input');
    input?.focus();
  }, [editingId]);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- sync DOM width/font with zoom ref each paint
  useLayoutEffect(() => {
    const block = pageBlockRef.current;
    if (!block) {
      return;
    }
    const width = inkPageWidth(zoomRef.current);
    if (block.style.width !== width) {
      block.style.width = width;
    }
    const nextFont = Math.max(10, block.clientHeight * MAINTENANCE_INK_TEXT_FONT_RATIO);
    if (Math.abs(nextFont - textFontPx) > 0.5) {
      setTextFontPx(nextFont);
    }
  });

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const document = await loadMaintenancePdfDocument(pdfUrl);
        if (!cancelled) {
          setPdf(document);
          setLoadError(null);
        } else {
          void document.cleanup();
        }
      } catch (error: unknown) {
        if (!cancelled) {
          setPdf(null);
          setLoadError(
            error instanceof Error ? error.message : 'Die PDF konnte nicht geladen werden.',
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pdfUrl]);

  useEffect(() => {
    if (!pdf) {
      return;
    }
    let cancelled = false;
    void (async () => {
      const pdfPage = await pdf.getPage(page);
      const viewport = pdfPage.getViewport({ scale: 2 });
      const canvas = canvasRef.current;
      if (!canvas || cancelled) {
        return;
      }
      const context = canvas.getContext('2d');
      if (!context) {
        return;
      }
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      await pdfPage.render({ canvasContext: context, viewport, canvas }).promise;
    })().catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [page, pdf]);

  useEffect(() => {
    if (!dirty || disabled) {
      return;
    }
    const handle = window.setTimeout(() => {
      void saveRef
        .current({
          page,
          body: {
            revision: revisionToSave,
            strokes: committedRef.current.strokes,
            texts: committedRef.current.texts,
          },
        })
        .then((result) => {
          clearInkPageDraft(protocolId, page);
          setLocalRevision(result.revision);
          setDirty(false);
          onSaved(result.revision);
        })
        .catch((error: unknown) => {
          if (error instanceof ApiRequestError && error.error.statusCode === 409) {
            clearInkDraft(protocolId);
            toast.error('Konflikt – bitte Seite neu laden.');
            onConflict();
            return;
          }
          toast.error(
            error instanceof ApiRequestError
              ? error.message
              : 'Die Stiftschicht konnte nicht gespeichert werden.',
          );
        });
    }, 900);
    return () => window.clearTimeout(handle);
  }, [dirty, disabled, onConflict, onSaved, page, protocolId, revisionToSave, strokes, texts]);

  function trackedTouches(): TrackedPointer[] {
    return [...pointersRef.current.values()].filter((pointer) => pointer.type === 'touch');
  }

  function fitPage() {
    zoomRef.current = 1;
    pinchRef.current = null;
    const block = pageBlockRef.current;
    if (block) {
      block.style.width = inkPageWidth(1);
    }
    const scroller = scrollerRef.current;
    if (scroller) {
      scroller.scrollLeft = 0;
      scroller.scrollTop = 0;
    }
  }

  function resetView() {
    fitPage();
    pointersRef.current.clear();
    activeRef.current = null;
    eraseWorkingRef.current = null;
    draftTextRef.current = null;
    textDragRef.current = null;
    setEditingId(null);
  }

  async function toggleFullscreen() {
    if (isFullscreen) {
      setIsFullscreen(false);
      setPageTurn(0);
      return;
    }
    try {
      await requestDeviceOrientation();
    } catch {
      // Gyro permission is optional; the page still follows the screen.
    }
    setIsFullscreen(true);
  }

  useEffect(() => {
    if (!isFullscreen) {
      return;
    }
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const measure = () => {
      const stage = stageRef.current;
      if (!stage) {
        return;
      }
      setStageSize({ width: stage.clientWidth, height: stage.clientHeight });
    };
    measure();
    const onResize = () => {
      measure();
      if (isScreenLandscape(window.innerWidth, window.innerHeight)) {
        setPageTurn(0);
      }
    };
    const onOrient = (event: DeviceOrientationEvent) => {
      if (event.beta == null || event.gamma == null) {
        return;
      }
      const landscape = isScreenLandscape(window.innerWidth, window.innerHeight);
      setPageTurn((current) => nextPageTurn(current, event.beta ?? 0, event.gamma ?? 0, landscape));
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsFullscreen(false);
        setPageTurn(0);
      }
    };
    window.addEventListener('resize', onResize);
    window.addEventListener('deviceorientation', onOrient);
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('resize', onResize);
      window.removeEventListener('deviceorientation', onOrient);
      window.removeEventListener('keydown', onKey);
    };
  }, [isFullscreen]);

  useLayoutEffect(() => {
    if (!isFullscreen) {
      return;
    }
    const stage = stageRef.current;
    if (stage) {
      const width = stage.clientWidth;
      const height = stage.clientHeight;
      if (width !== stageSize.width || height !== stageSize.height) {
        setStageSize({ width, height });
      }
    }
    fitPage();
  }, [isFullscreen, pageTurn, stageSize.height, stageSize.width]);

  function historyFor(pageNumber: number): InkPageHistory {
    return historyByPageRef.current.get(pageNumber) ?? emptyInkHistory();
  }

  function setPageHistory(pageNumber: number, history: InkPageHistory) {
    historyByPageRef.current.set(pageNumber, history);
    setCanUndo(history.past.length > 0);
    setCanRedo(history.future.length > 0);
  }

  function applyCommitted(next: InkPageState) {
    const state = { strokes: next.strokes, texts: persistableInkTexts(next.texts) };
    committedRef.current = state;
    writeInkPageDraft(protocolId, page, state);
    setStrokes(state.strokes);
    setTexts(state.texts);
    setDirty(true);
  }

  function previewStrokes(next: MaintenanceInkStroke[]) {
    setStrokes(next);
  }

  function previewTexts(next: MaintenanceInkText[]) {
    setTexts(next);
  }

  function finishOpenChange() {
    const active = activeRef.current;
    if (active && active.points.length > 0) {
      replaceCommitted({
        strokes: [...committedRef.current.strokes, strokeFromActive(active)],
        texts: committedRef.current.texts,
      });
    }
    activeRef.current = null;
    const erasing = eraseWorkingRef.current;
    if (erasing) {
      replaceCommitted(erasing);
      eraseWorkingRef.current = null;
    }
    commitDraftText();
  }

  function showPage(next: number) {
    finishOpenChange();
    writeInkPageDraft(protocolId, page, committedRef.current);
    const loaded = pageStateFor(protocolId, next, layers);
    committedRef.current = loaded.state;
    resetView();
    setPage(next);
    setStrokes(loaded.state.strokes);
    setTexts(loaded.state.texts);
    setDirty(loaded.dirty);
    const history = historyFor(next);
    setCanUndo(history.past.length > 0);
    setCanRedo(history.future.length > 0);
  }

  function replaceCommitted(next: InkPageState) {
    if (inkStatesEqual(committedRef.current, next)) {
      return;
    }
    setPageHistory(page, pushInkHistory(historyFor(page), committedRef.current));
    applyCommitted(next);
  }

  function undo() {
    const result = undoInkHistory(historyFor(page), committedRef.current);
    if (!result) {
      return;
    }
    draftTextRef.current = null;
    setEditingId(null);
    setPageHistory(page, result.history);
    applyCommitted(result.next);
  }

  function redo() {
    const result = redoInkHistory(historyFor(page), committedRef.current);
    if (!result) {
      return;
    }
    draftTextRef.current = null;
    setEditingId(null);
    setPageHistory(page, result.history);
    applyCommitted(result.next);
  }

  function visibleTexts(): MaintenanceInkText[] {
    const draft = draftTextRef.current;
    if (!draft) {
      return texts;
    }
    if (texts.some((box) => box.id === draft.id)) {
      return texts;
    }
    return [...texts, draft];
  }

  function commitDraftText() {
    const draft = draftTextRef.current;
    if (!draft) {
      return;
    }
    const sanitized = maintenanceInkSanitizeText(draft.text);
    draftTextRef.current = null;
    setEditingId(null);
    if (!sanitized) {
      previewTexts(committedRef.current.texts);
      return;
    }
    replaceCommitted({
      strokes: committedRef.current.strokes,
      texts: persistableInkTexts([
        ...committedRef.current.texts.filter((box) => box.id !== draft.id),
        { ...draft, text: sanitized },
      ]),
    });
  }

  function placeText(point: MaintenanceInkPoint) {
    commitDraftText();
    if (committedRef.current.texts.length >= MAINTENANCE_INK_TEXT_MAX) {
      return;
    }
    const box: MaintenanceInkText = {
      id: crypto.randomUUID(),
      x: point.x,
      y: point.y,
      text: '',
      color: inkColor,
    };
    draftTextRef.current = box;
    previewTexts([...committedRef.current.texts, box]);
    setEditingId(box.id);
  }

  function cancelFingerInk() {
    if (activeRef.current?.fromTouch) {
      activeRef.current = null;
      previewStrokes(committedRef.current.strokes);
    }
    if (eraseWorkingRef.current) {
      eraseWorkingRef.current = null;
      previewStrokes(committedRef.current.strokes);
      previewTexts(committedRef.current.texts);
    }
  }

  function textsHit(point: MaintenanceInkPoint): string[] {
    const block = pageBlockRef.current;
    if (!block) {
      return [];
    }
    const rect = block.getBoundingClientRect();
    const x = rect.left + point.x * rect.width;
    const y = rect.top + point.y * rect.height;
    const hits: string[] = [];
    for (const [id, element] of textElsRef.current) {
      const bounds = element.getBoundingClientRect();
      if (x >= bounds.left && x <= bounds.right && y >= bounds.top && y <= bounds.bottom) {
        hits.push(id);
      }
    }
    return hits;
  }

  function eraseAt(points: MaintenanceInkPoint[], aspect: number) {
    let next = eraseWorkingRef.current ?? {
      strokes: committedRef.current.strokes,
      texts: [
        ...committedRef.current.texts,
        ...(draftTextRef.current &&
        !committedRef.current.texts.some((box) => box.id === draftTextRef.current?.id)
          ? [draftTextRef.current]
          : []),
      ],
    };
    let changed = false;
    for (const point of points) {
      const strokes = next.strokes.filter((stroke) => !strokeHit(stroke, point, aspect));
      const hitIds = new Set(textsHit(point));
      const nextTexts = next.texts.filter((box) => !hitIds.has(box.id));
      if (strokes.length !== next.strokes.length || nextTexts.length !== next.texts.length) {
        next = { strokes, texts: nextTexts };
        changed = true;
      }
    }
    if (changed) {
      if (draftTextRef.current && !next.texts.some((box) => box.id === draftTextRef.current?.id)) {
        draftTextRef.current = null;
        setEditingId(null);
      }
      eraseWorkingRef.current = next;
      previewStrokes(next.strokes);
      previewTexts(next.texts);
    }
  }

  function beginPinch() {
    const touches = trackedTouches().filter((pointer) => pointer.role === 'pinch');
    const first = touches[0];
    const second = touches[1];
    const scroller = scrollerRef.current;
    const block = pageBlockRef.current;
    if (!first || !second || !scroller || !block) {
      return;
    }
    const rect = scroller.getBoundingClientRect();
    const viewportLeft = rect.left + scroller.clientLeft;
    const viewportTop = rect.top + scroller.clientTop;
    const midX = (first.x + second.x) / 2;
    const midY = (first.y + second.y) / 2;
    pinchRef.current = {
      startDistance: Math.max(Math.hypot(first.x - second.x, first.y - second.y), 1),
      startZoom: zoomRef.current,
      originX: block.offsetLeft,
      originY: block.offsetTop,
      contentX: scroller.scrollLeft + (midX - viewportLeft),
      contentY: scroller.scrollTop + (midY - viewportTop),
    };
  }

  function applyPinch() {
    const pinch = pinchRef.current;
    const scroller = scrollerRef.current;
    const block = pageBlockRef.current;
    if (!pinch || !scroller || !block) {
      return;
    }
    const touches = trackedTouches().filter((pointer) => pointer.role === 'pinch');
    const first = touches[0];
    const second = touches[1];
    if (!first || !second) {
      return;
    }
    const distance = Math.hypot(first.x - second.x, first.y - second.y);
    const nextZoom = zoomFromPinch(pinch.startZoom, pinch.startDistance, distance);
    zoomRef.current = nextZoom;
    block.style.width = inkPageWidth(nextZoom);
    void block.offsetWidth;
    const rect = scroller.getBoundingClientRect();
    const next = scrollToKeepPoint({
      originX: pinch.originX,
      originY: pinch.originY,
      contentX: pinch.contentX,
      contentY: pinch.contentY,
      scale: nextZoom / pinch.startZoom,
      midX: (first.x + second.x) / 2,
      midY: (first.y + second.y) / 2,
      scrollerLeft: rect.left + scroller.clientLeft,
      scrollerTop: rect.top + scroller.clientTop,
    });
    scroller.scrollLeft = next.scrollLeft;
    scroller.scrollTop = next.scrollTop;
  }

  function releasePointer(event: React.PointerEvent<SVGSVGElement>) {
    try {
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
    } catch {
      // Pointer is already gone.
    }
  }

  function onPointerDown(event: React.PointerEvent<SVGSVGElement>) {
    if (disabled) {
      return;
    }
    const pointerType = normalizePointerType(event.pointerType);
    if (pointerType === 'pen') {
      penSeenRef.current = true;
    }
    const touches = trackedTouches();
    const role = roleForNewPointer({
      pointerType,
      tool,
      penSeen: penSeenRef.current,
      penDown: [...pointersRef.current.values()].some((pointer) => pointer.type === 'pen'),
      trackedTouchCount: touches.length,
      largeContact: pointerType === 'touch' && isLargeContact(event.width, event.height),
    });
    if (role === 'ignore') {
      return;
    }
    if (role === 'pinch') {
      if (shouldCancelFingerInk(touches.map((touch) => touch.role))) {
        cancelFingerInk();
      }
      for (const touch of touches) {
        touch.role = 'pinch';
        touch.pan = null;
      }
    }
    const tracked: TrackedPointer = {
      id: event.pointerId,
      type: pointerType,
      x: event.clientX,
      y: event.clientY,
      role,
      pan: null,
      originX: event.clientX,
      originY: event.clientY,
    };
    pointersRef.current.set(event.pointerId, tracked);
    if (shouldCapturePointer(pointerType, role)) {
      event.currentTarget.setPointerCapture(event.pointerId);
    }
    if (role === 'draw') {
      const point = unitPoint(
        event.currentTarget.getBoundingClientRect(),
        event.clientX,
        event.clientY,
      );
      const active: ActiveStroke = {
        pointerId: event.pointerId,
        fromTouch: pointerType === 'touch',
        points: [point],
        color: inkColor,
        width: inkWidth,
      };
      activeRef.current = active;
      previewStrokes([...committedRef.current.strokes, strokeFromActive(active)]);
      return;
    }
    if (role === 'erase') {
      eraseAt(pointsFromPointer(event), aspectFromEvent(event));
      return;
    }
    if (role === 'pan') {
      const scroller = scrollerRef.current;
      if (!scroller) {
        return;
      }
      tracked.pan = {
        scrollLeft: scroller.scrollLeft,
        scrollTop: scroller.scrollTop,
        clientX: event.clientX,
        clientY: event.clientY,
      };
      return;
    }
    if (role === 'pinch') {
      beginPinch();
    }
  }

  function onPointerMove(event: React.PointerEvent<SVGSVGElement>) {
    if (disabled) {
      return;
    }
    const tracked = pointersRef.current.get(event.pointerId);
    if (!tracked) {
      return;
    }
    tracked.x = event.clientX;
    tracked.y = event.clientY;
    if (tracked.role === 'draw') {
      const active = activeRef.current;
      if (active?.pointerId !== event.pointerId) {
        return;
      }
      const next = appendStrokePoints(active.points, pointsFromPointer(event));
      if (!next) {
        return;
      }
      active.points = next;
      previewStrokes([...committedRef.current.strokes, strokeFromActive(active)]);
      return;
    }
    if (tracked.role === 'erase') {
      if (tracked.type !== 'touch' && event.buttons !== 1) {
        return;
      }
      eraseAt(pointsFromPointer(event), aspectFromEvent(event));
      return;
    }
    if (tracked.role === 'pan' && tracked.pan) {
      const scroller = scrollerRef.current;
      if (!scroller) {
        return;
      }
      const next = scrollForPan(tracked.pan, event.clientX, event.clientY);
      scroller.scrollLeft = next.scrollLeft;
      scroller.scrollTop = next.scrollTop;
      return;
    }
    if (tracked.role === 'pinch') {
      applyPinch();
    }
  }

  function onPointerEnd(event: React.PointerEvent<SVGSVGElement>) {
    const tracked = pointersRef.current.get(event.pointerId);
    pointersRef.current.delete(event.pointerId);
    releasePointer(event);
    if (!tracked) {
      return;
    }
    if (tracked.role === 'draw') {
      const active = activeRef.current;
      if (active?.pointerId === event.pointerId) {
        activeRef.current = null;
        if (active.points.length > 0) {
          replaceCommitted({
            strokes: [...committedRef.current.strokes, strokeFromActive(active)],
            texts: committedRef.current.texts,
          });
        }
      }
    }
    if (tracked.role === 'erase') {
      const erasing = eraseWorkingRef.current;
      eraseWorkingRef.current = null;
      if (erasing) {
        replaceCommitted({
          strokes: erasing.strokes,
          texts: persistableInkTexts(erasing.texts),
        });
      }
    }
    if (tracked.role === 'text') {
      const moved = Math.hypot(tracked.x - tracked.originX, tracked.y - tracked.originY);
      if (moved <= TEXT_TAP_PX) {
        const point = unitPoint(event.currentTarget.getBoundingClientRect(), tracked.x, tracked.y);
        placeText(point);
      }
    }
    const pinchTouches = trackedTouches().filter((pointer) => pointer.role === 'pinch');
    if (!pinchRef.current && tracked.role !== 'pinch') {
      return;
    }
    if (pinchTouches.length >= 2) {
      beginPinch();
      return;
    }
    pinchRef.current = null;
    const left = pinchTouches[0];
    const scroller = scrollerRef.current;
    if (!left || !scroller) {
      return;
    }
    left.role = 'pan';
    left.pan = {
      scrollLeft: scroller.scrollLeft,
      scrollTop: scroller.scrollTop,
      clientX: left.x,
      clientY: left.y,
    };
  }

  function onTextPointerDown(event: React.PointerEvent<HTMLDivElement>, box: MaintenanceInkText) {
    if (disabled || tool !== 'text') {
      return;
    }
    event.stopPropagation();
    if (event.target instanceof HTMLInputElement) {
      setEditingId(box.id);
      return;
    }
    if (normalizePointerType(event.pointerType) !== 'touch') {
      event.currentTarget.setPointerCapture(event.pointerId);
    }
    textDragRef.current = {
      id: box.id,
      originX: event.clientX,
      originY: event.clientY,
      startX: box.x,
      startY: box.y,
      moved: false,
    };
  }

  function onTextPointerMove(event: React.PointerEvent<HTMLDivElement>) {
    const drag = textDragRef.current;
    const block = pageBlockRef.current;
    if (!drag || !block) {
      return;
    }
    const rect = block.getBoundingClientRect();
    const dx = (event.clientX - drag.originX) / (rect.width || 1);
    const dy = (event.clientY - drag.originY) / (rect.height || 1);
    if (Math.hypot(event.clientX - drag.originX, event.clientY - drag.originY) > TEXT_TAP_PX) {
      drag.moved = true;
      setEditingId(null);
    }
    if (!drag.moved) {
      return;
    }
    const x = Math.min(1, Math.max(0, drag.startX + dx));
    const y = Math.min(1, Math.max(0, drag.startY + dy));
    const update = (box: MaintenanceInkText) => (box.id === drag.id ? { ...box, x, y } : box);
    if (draftTextRef.current?.id === drag.id) {
      draftTextRef.current = { ...draftTextRef.current, x, y };
    }
    previewTexts(visibleTexts().map(update));
  }

  function onTextPointerUp(event: React.PointerEvent<HTMLDivElement>) {
    const drag = textDragRef.current;
    textDragRef.current = null;
    try {
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
    } catch {
      // Pointer is already gone.
    }
    if (!drag) {
      return;
    }
    if (!drag.moved) {
      setEditingId(drag.id);
      return;
    }
    const current = visibleTexts().find((box) => box.id === drag.id);
    if (!current) {
      return;
    }
    if (draftTextRef.current?.id === drag.id) {
      draftTextRef.current = current;
      previewTexts([...committedRef.current.texts.filter((box) => box.id !== current.id), current]);
      return;
    }
    replaceCommitted({
      strokes: committedRef.current.strokes,
      texts: persistableInkTexts(
        committedRef.current.texts.map((box) => (box.id === current.id ? current : box)),
      ),
    });
  }

  function onTextValueChange(id: string, value: string) {
    const sanitized = maintenanceInkSanitizeText(value);
    const update = (box: MaintenanceInkText) => (box.id === id ? { ...box, text: sanitized } : box);
    if (draftTextRef.current?.id === id) {
      draftTextRef.current = { ...draftTextRef.current, text: sanitized };
    }
    previewTexts(visibleTexts().map(update));
  }

  function onTextBlur(id: string) {
    if (textDragRef.current) {
      return;
    }
    const current = visibleTexts().find((box) => box.id === id);
    if (draftTextRef.current?.id === id) {
      commitDraftText();
      return;
    }
    if (!current) {
      return;
    }
    const sanitized = maintenanceInkSanitizeText(current.text);
    setEditingId((open) => (open === id ? null : open));
    if (!sanitized) {
      replaceCommitted({
        strokes: committedRef.current.strokes,
        texts: committedRef.current.texts.filter((box) => box.id !== id),
      });
      return;
    }
    replaceCommitted({
      strokes: committedRef.current.strokes,
      texts: persistableInkTexts(
        committedRef.current.texts.map((box) =>
          box.id === id ? { ...box, text: sanitized } : box,
        ),
      ),
    });
  }

  return (
    <div
      className={cn(
        'w-full space-y-3',
        isFullscreen && 'fixed inset-0 z-[80] flex flex-col space-y-0 bg-background p-2',
      )}
    >
      <div
        className={cn(
          'sticky z-20 flex flex-wrap items-center justify-start gap-x-2 gap-y-1 bg-background py-2',
          isFullscreen
            ? 'top-0 left-0'
            : 'top-[calc(3.5rem+2.75rem+1px)] sm:top-[calc(4rem+2.75rem+1px)]',
        )}
      >
        {!disabled ? (
          <>
            <div className="flex flex-wrap gap-1">
              <Button
                type="button"
                className="h-11 min-h-11 w-11 min-w-11 p-0"
                variant={tool === 'draw' ? 'default' : 'outline'}
                aria-label="Stift"
                onClick={() => setTool('draw')}
              >
                <PencilIcon className="size-4" aria-hidden />
              </Button>
              <Button
                type="button"
                className="h-11 min-h-11 w-11 min-w-11 p-0"
                variant={tool === 'erase' ? 'default' : 'outline'}
                aria-label="Radierer"
                onClick={() => setTool('erase')}
              >
                <EraserIcon className="size-4" aria-hidden />
              </Button>
              <Button
                type="button"
                className="h-11 min-h-11 w-11 min-w-11 p-0"
                variant={tool === 'scroll' ? 'default' : 'outline'}
                aria-label="Verschieben"
                onClick={() => setTool('scroll')}
              >
                <HandIcon className="size-4" aria-hidden />
              </Button>
              <Button
                type="button"
                className="h-11 min-h-11 w-11 min-w-11 p-0"
                variant={tool === 'text' ? 'default' : 'outline'}
                aria-label="Text"
                onClick={() => setTool('text')}
              >
                <TypeIcon className="size-4" aria-hidden />
              </Button>
            </div>
            <div className="flex flex-wrap gap-1">
              <Button
                type="button"
                variant="outline"
                className="h-11 min-h-11 w-11 min-w-11 p-0"
                disabled={!canUndo}
                aria-label="Rückgängig"
                onClick={undo}
              >
                <Undo2Icon className="size-4" aria-hidden />
              </Button>
              <Button
                type="button"
                variant="outline"
                className="h-11 min-h-11 w-11 min-w-11 p-0"
                disabled={!canRedo}
                aria-label="Wiederholen"
                onClick={redo}
              >
                <Redo2Icon className="size-4" aria-hidden />
              </Button>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  className="h-11 min-h-11 w-11 min-w-11 p-0"
                  aria-label="Stiftfarbe"
                >
                  <span
                    className="size-4 rounded-full border"
                    style={{ backgroundColor: inkColor }}
                    aria-hidden
                  />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className={isFullscreen ? 'z-[120]' : undefined}>
                <DropdownMenuRadioGroup
                  value={inkColor}
                  onValueChange={(value) => {
                    const color = parseInkColor(value);
                    if (color) {
                      setInkColor(color);
                    }
                  }}
                >
                  {MAINTENANCE_INK_COLORS.map((color) => (
                    <DropdownMenuRadioItem key={color} value={color} className="min-h-11 gap-2">
                      <span
                        className="size-4 rounded-full border"
                        style={{ backgroundColor: color }}
                        aria-hidden
                      />
                      {INK_COLOR_LABELS[color]}
                    </DropdownMenuRadioItem>
                  ))}
                </DropdownMenuRadioGroup>
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  className="h-11 min-h-11 w-11 min-w-11 p-0"
                  aria-label="Stiftstärke"
                >
                  <svg viewBox="0 0 32 16" className="h-4 w-8" aria-hidden>
                    <line
                      x1="2"
                      y1="8"
                      x2="30"
                      y2="8"
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeWidth={inkWidth * 500}
                    />
                  </svg>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className={isFullscreen ? 'z-[120]' : undefined}>
                <DropdownMenuRadioGroup
                  value={String(inkWidth)}
                  onValueChange={(value) => {
                    const width = parseInkWidth(value);
                    if (width) {
                      setInkWidth(width);
                    }
                  }}
                >
                  {MAINTENANCE_INK_WIDTHS.map((width) => (
                    <DropdownMenuRadioItem
                      key={width}
                      value={String(width)}
                      className="min-h-11 gap-2"
                    >
                      <svg viewBox="0 0 32 16" className="h-4 w-8" aria-hidden>
                        <line
                          x1="2"
                          y1="8"
                          x2="30"
                          y2="8"
                          stroke="currentColor"
                          strokeLinecap="round"
                          strokeWidth={width * 500}
                        />
                      </svg>
                      {INK_WIDTH_LABELS[width]}
                    </DropdownMenuRadioItem>
                  ))}
                </DropdownMenuRadioGroup>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              type="button"
              variant="outline"
              className="h-11 min-h-11 w-11 min-w-11 p-0"
              aria-label="Einpassen"
              onClick={fitPage}
            >
              <ScanIcon className="size-4" aria-hidden />
            </Button>
          </>
        ) : null}
        <div className="flex flex-wrap items-center gap-1">
          <Button
            type="button"
            variant="outline"
            className="h-11 min-h-11 w-11 min-w-11 p-0"
            disabled={page <= 1}
            aria-label="Seite zurück"
            onClick={() => showPage(page - 1)}
          >
            <ChevronLeftIcon className="size-4" aria-hidden />
          </Button>
          <span className="flex min-h-11 items-center px-1 text-sm text-muted-foreground">
            Seite {page} von {pageCount}
          </span>
          <Button
            type="button"
            variant="outline"
            className="h-11 min-h-11 w-11 min-w-11 p-0"
            disabled={page >= pageCount}
            aria-label="Seite weiter"
            onClick={() => showPage(page + 1)}
          >
            <ChevronRightIcon className="size-4" aria-hidden />
          </Button>
          <Button
            type="button"
            variant={isFullscreen ? 'default' : 'outline'}
            className="h-11 min-h-11 w-11 min-w-11 p-0"
            aria-label={isFullscreen ? 'Vollbild beenden' : 'Vollbild'}
            onClick={() => void toggleFullscreen()}
          >
            {isFullscreen ? (
              <Minimize2Icon className="size-4" aria-hidden />
            ) : (
              <Maximize2Icon className="size-4" aria-hidden />
            )}
          </Button>
        </div>
      </div>
      <div
        ref={stageRef}
        className={cn(isFullscreen ? 'relative min-h-0 flex-1 overflow-hidden' : 'w-full')}
      >
        <div
          ref={scrollerRef}
          className={cn(
            'overflow-auto overscroll-contain rounded-md border bg-muted',
            isFullscreen ? 'relative' : 'relative h-[min(70dvh,70rem)] w-full',
          )}
          style={
            isFullscreen
              ? rotatedScrollerStyle(pageTurn, stageSize.width, stageSize.height)
              : undefined
          }
        >
          {loadError ? <p className="p-6 text-sm text-destructive">{loadError}</p> : null}
          <div ref={pageBlockRef} className="relative">
            <canvas ref={canvasRef} className="block h-auto w-full" />
            <svg
              viewBox="0 0 1 1"
              preserveAspectRatio="none"
              className="absolute inset-0 h-full w-full"
              style={{
                touchAction: 'none',
                pointerEvents: disabled ? 'none' : 'auto',
              }}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerEnd}
              onPointerCancel={onPointerEnd}
            >
              {strokes.map((stroke, index) => {
                const style = maintenanceInkPreviewStyle(stroke);
                return (
                  <polyline
                    key={`${page}-${index}-${stroke.points.length}`}
                    points={stroke.points.map((point) => `${point.x},${point.y}`).join(' ')}
                    fill="none"
                    stroke={style.color}
                    strokeWidth={style.width}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                );
              })}
            </svg>
            {texts.map((box) => (
              // Paper highlight stays light so typed notes remain readable on the sheet.
              <div
                key={box.id}
                ref={(element) => {
                  if (element) {
                    textElsRef.current.set(box.id, element);
                  } else {
                    textElsRef.current.delete(box.id);
                  }
                }}
                className="absolute z-10 max-w-[80%] min-w-[4rem] rounded-sm border border-transparent bg-white/80 px-0.5 shadow-sm"
                style={{
                  left: `${box.x * 100}%`,
                  top: `${box.y * 100}%`,
                  color: box.color ?? MAINTENANCE_INK_DEFAULT_COLOR,
                  fontSize: `${textFontPx}px`,
                  pointerEvents: disabled || tool !== 'text' ? 'none' : 'auto',
                  touchAction: 'none',
                }}
                onPointerDown={(event) => onTextPointerDown(event, box)}
                onPointerMove={onTextPointerMove}
                onPointerUp={onTextPointerUp}
                onPointerCancel={onTextPointerUp}
              >
                <input
                  value={box.text}
                  maxLength={200}
                  readOnly={disabled ? true : tool !== 'text'}
                  onChange={(event) => onTextValueChange(box.id, event.target.value)}
                  onFocus={() => setEditingId(box.id)}
                  onBlur={() => onTextBlur(box.id)}
                  aria-label="Text auf der PDF"
                  className="block w-full min-w-[4rem] bg-transparent font-sans outline-none"
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
